import { useState } from "react";
import { KeyRound, Eye, EyeOff } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

function PwField({ id, label, value, onChange, testId, autoComplete }) {
  const [show, setShow] = useState(false);
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className="text-sm text-[#334155]">{label}</Label>
      <div className="relative">
        <Input
          id={id}
          type={show ? "text" : "password"}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          autoComplete={autoComplete}
          data-testid={testId}
          className="pr-10"
        />
        <button
          type="button"
          tabIndex={-1}
          aria-label={show ? "Ocultar contraseña" : "Mostrar contraseña"}
          onClick={() => setShow((s) => !s)}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-[#64748B] hover:text-[#0F172A] transition-colors"
        >
          {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
}

export default function ChangePasswordDialog({ open, onOpenChange }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const resetState = () => {
    setCurrent("");
    setNext("");
    setConfirm("");
    setError("");
    setBusy(false);
  };

  const handleOpenChange = (val) => {
    if (!val) resetState();
    onOpenChange(val);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!current || !next || !confirm) {
      setError("Todos los campos son obligatorios.");
      return;
    }
    if (next.length < 8) {
      setError("La nueva contraseña debe tener al menos 8 caracteres.");
      return;
    }
    if (next !== confirm) {
      setError("Las contraseñas nuevas no coinciden.");
      return;
    }
    if (next === current) {
      setError("La nueva contraseña debe ser distinta a la actual.");
      return;
    }

    setBusy(true);
    try {
      await api.post("/auth/change-password", {
        current_password: current,
        new_password: next,
      });
      toast.success("Contraseña actualizada correctamente.");
      resetState();
      onOpenChange(false);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent data-testid="change-password-dialog" className="sm:max-w-[440px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#0F172A]">
            <span className="inline-flex w-8 h-8 rounded-full bg-[#D1FAE5] text-[#147D5C] items-center justify-center">
              <KeyRound className="w-4 h-4" />
            </span>
            Cambiar contraseña
          </DialogTitle>
          <DialogDescription className="text-sm text-[#64748B]">
            Ingresa tu contraseña actual y elige una nueva. Mínimo 8 caracteres.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <PwField
            id="cp-current"
            label="Contraseña actual"
            value={current}
            onChange={setCurrent}
            autoComplete="current-password"
            testId="cp-current-input"
          />
          <PwField
            id="cp-new"
            label="Nueva contraseña"
            value={next}
            onChange={setNext}
            autoComplete="new-password"
            testId="cp-new-input"
          />
          <PwField
            id="cp-confirm"
            label="Confirmar nueva contraseña"
            value={confirm}
            onChange={setConfirm}
            autoComplete="new-password"
            testId="cp-confirm-input"
          />

          {error && (
            <div
              data-testid="cp-error"
              className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-[#B91C1C]"
            >
              {error}
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={busy}
              data-testid="cp-cancel-btn"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={busy}
              className="bg-[#1D9E75] hover:bg-[#147D5C] text-white"
              data-testid="cp-submit-btn"
            >
              {busy ? "Guardando..." : "Actualizar contraseña"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
